import React, { useState, useEffect, useRef } from 'react';
import { Search, ChevronDown, Check, X } from 'lucide-react';
import axiosInstance from '../api/axiosInstance';

const SearchableInvoiceDropdown = ({ 
    value = '', 
    onChange, 
    placeholder = 'Select Invoice...', 
    disabled = false,
    className = '',
    error = false
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [hasInitialLoad, setHasInitialLoad] = useState(false);
    const isInitialMount = useRef(true);
    const dropdownRef = useRef(null);
    const searchInputRef = useRef(null);

    // Find selected invoice details
    useEffect(() => {
        if (value && invoices.length > 0) {
            const found = invoices.find(inv => inv.id.toString() === value.toString());
            setSelectedInvoice(found);
        } else {
            setSelectedInvoice(null);
        }
    }, [value, invoices]);

    // Debounced search - only search when user actually types (not on initial load)
    useEffect(() => {
        if (!isOpen || !hasInitialLoad || isInitialMount.current) return;
        
        const timeoutId = setTimeout(() => {
            fetchInvoices(searchTerm);
        }, 300);

        return () => clearTimeout(timeoutId);
    }, [searchTerm, isOpen, hasInitialLoad]);

    // Fetch invoices only when dropdown opens for the first time
    useEffect(() => {
        if (isOpen && !hasInitialLoad) {
            fetchInvoices('');
            setHasInitialLoad(true);
            isInitialMount.current = false;
        }
    }, [isOpen, hasInitialLoad]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const fetchInvoices = async (search = '') => {
        if (loading) return; // Prevent multiple simultaneous calls
        
        setLoading(true);
        try {
            let params = {};
            if (search.trim()) {
                // If search term is numeric, search by mobile or invoice ID
                if (/^\d+$/.test(search.trim())) {
                    params.customer_mobile = search.trim();
                } else {
                    // If search term contains letters, search by customer name
                    params.customer_name = search.trim();
                }
            }
            
            const response = await axiosInstance.get('/sales-returns/invoices', { params });
            setInvoices(response.data.invoices || []);
        } catch (error) {
            console.error('Error fetching invoices:', error);
            setInvoices([]);
        } finally {
            setLoading(false);
        }
    };

    const handleInvoiceSelect = (invoice) => {
        setSelectedInvoice(invoice);
        onChange(invoice);
        setIsOpen(false);
        setSearchTerm('');
    };

    const handleToggleDropdown = () => {
        if (disabled) return;
        setIsOpen(!isOpen);
        if (!isOpen) {
            setTimeout(() => {
                searchInputRef.current?.focus();
            }, 100);
        }
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const handleClear = () => {
        setSelectedInvoice(null);
        onChange(null);
        setSearchTerm('');
    };

    const filteredInvoices = invoices.filter(invoice =>
        invoice.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        invoice.mobile_number?.includes(searchTerm) ||
        invoice.id.toString().includes(searchTerm)
    );

    return (
        <div className={`relative ${className}`} ref={dropdownRef}>
            {/* Main Input/Button */}
            <div
                onClick={handleToggleDropdown}
                className={`
                    w-full px-3 py-2.5 bg-white border rounded-lg cursor-pointer
                    flex items-center justify-between
                    ${error ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : 'border-gray-200 focus:border-teal-500 focus:ring-teal-500'}
                    ${disabled ? 'bg-gray-50 cursor-not-allowed' : 'hover:border-gray-300'}
                    focus:outline-none focus:ring-2 focus:ring-opacity-50
                    transition-colors duration-200
                `}
            >
                <div className="flex-1 text-left">
                    {selectedInvoice ? (
                        <div className="text-sm">
                            <div className="font-medium text-gray-900">
                                Invoice #{selectedInvoice.id} - {selectedInvoice.name}
                            </div>
                            <div className="text-gray-500">
                                {selectedInvoice.mobile_number} - ₹{parseFloat(selectedInvoice.total_amount).toFixed(2)}
                            </div>
                        </div>
                    ) : (
                        <span className="text-gray-400 text-sm">{placeholder}</span>
                    )}
                </div>
                <div className="flex items-center space-x-1">
                    {selectedInvoice && !disabled && (
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleClear();
                            }}
                            className="text-gray-400 hover:text-gray-600 p-1"
                        >
                            <X size={16} />
                        </button>
                    )}
                    <ChevronDown 
                        size={16} 
                        className={`text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                    />
                </div>
            </div>

            {/* Dropdown Menu */}
            {isOpen && (
                <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-hidden">
                    {/* Search Input */}
                    <div className="p-3 border-b border-gray-100">
                        <div className="relative">
                            <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                            <input
                                ref={searchInputRef}
                                type="text"
                                placeholder="Search by customer name, mobile, or invoice ID..."
                                value={searchTerm}
                                onChange={handleSearchChange}
                                className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded focus:outline-none focus:border-teal-500 text-sm"
                            />
                        </div>
                    </div>

                    {/* Options List */}
                    <div className="max-h-60 overflow-y-auto">
                        {loading ? (
                            <div className="p-3 text-center text-gray-500 text-sm">
                                Searching invoices...
                            </div>
                        ) : filteredInvoices.length > 0 ? (
                            filteredInvoices.map((invoice) => (
                                <div
                                    key={invoice.id}
                                    onClick={() => handleInvoiceSelect(invoice)}
                                    className={`
                                        px-3 py-3 cursor-pointer hover:bg-gray-50 
                                        border-b border-gray-50 last:border-b-0
                                        ${selectedInvoice?.id === invoice.id ? 'bg-teal-50 text-teal-700' : 'text-gray-900'}
                                    `}
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex-1">
                                            <div className="font-medium text-sm">
                                                Invoice #{invoice.id} - {invoice.name}
                                            </div>
                                            <div className="text-xs text-gray-500 mt-1">
                                                {invoice.mobile_number} • {new Date(invoice.invoice_date).toLocaleDateString()} • ₹{parseFloat(invoice.total_amount).toFixed(2)}
                                            </div>
                                        </div>
                                        {selectedInvoice?.id === invoice.id && (
                                            <Check size={16} className="text-teal-600 ml-2" />
                                        )}
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="p-3 text-center text-gray-500 text-sm">
                                {searchTerm ? 'No invoices found matching your search' : 'No invoices available'}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default SearchableInvoiceDropdown;